import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from 'redux-form';
import { Button } from 'reactstrap';
import { ChromePicker } from 'react-color';

import './ColorPickerField.scss';

const color_to_stored = (color, with_alpha) => {
    if (with_alpha && color.rgb && color.rgb.a !== 1) {
        const int_value = Math.round(color.rgb.a * 255);
        const hex_alpha = int_value.toString(16).padStart(2, '0').toUpperCase();
        return `${color.hex}${hex_alpha}`;
    }
    return color.hex;
};

/**
 * Compact color picker with optional clear (redux-form or controlled).
 */
export const ColorPickerInput = ({ value, onChange, optional, with_alpha = false, disabled = false, emptyValue = '' }) => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const allow_clear = Boolean(optional);
    const stored = String(value ?? '').trim();
    const is_clear = !stored || stored.toLowerCase() === 'transparent';
    const display_value = is_clear ? '' : stored;
    const picker_color = display_value || '#ffffff';

    const apply_value = (next) => {
        const normalized = allow_clear && !next ? emptyValue : next;
        onChange?.(normalized);
    };

    return (
        <div className="ColorPickerField">
            <div className="input-row">
                <Button
                    type="button"
                    className="color-swatch-btn"
                    style={{ backgroundColor: display_value || 'transparent' }}
                    disabled={disabled}
                    onClick={() => !disabled && setVisible(true)}
                >
                    <span className="colorValue">
                        {display_value || t('layouts.color_transparent')}
                    </span>
                </Button>
                {allow_clear && display_value && !disabled && (
                    <Button
                        type="button"
                        color="link"
                        className="color-clear-btn"
                        title={t('layouts.color_clear_hint')}
                        aria-label={t('layouts.color_clear_hint')}
                        onClick={() => apply_value('')}
                    >
                        <i className="fa fa-trash-o" aria-hidden="true" />
                    </Button>
                )}
                {visible && (
                    <div className="popover">
                        <div className="cover" onClick={() => setVisible(false)} />
                        <ChromePicker
                            color={picker_color}
                            disableAlpha={!with_alpha}
                            onChange={color => apply_value(color_to_stored(color, with_alpha))}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

/** Controlled color picker for layout editors outside redux-form. */
export const LayoutColorPickerInput = ({ value, onChange, optional, with_alpha, disabled }) => (
    <ColorPickerInput value={value} onChange={onChange} optional={optional} with_alpha={with_alpha} disabled={disabled} />
);

const ColorPickerField = ({ name, optional, clearable, onChange, with_alpha, disabled, emptyValue }) => {
    const allow_clear = optional || clearable;

    const renderField = field => (
        <ColorPickerInput
            value={field.input.value}
            onChange={(next) => {
                field.input.onChange(next);
                onChange?.(next);
            }}
            optional={allow_clear}
            with_alpha={with_alpha}
            disabled={disabled}
            emptyValue={emptyValue}
        />
    );

    return (
        <Field
            required={!optional}
            component={renderField}
            disabled={disabled}
            className="form-control"
            type="text"
            name={name}
            id={name}
        />
    );
};

export default ColorPickerField;
